import { useState } from 'react';
import { Terminal, Database, Zap, RefreshCw, CheckCircle, XCircle, Lock, FileCode, HardDrive, Bug, Trash2 } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import '../styles/swagger-dark.css';

type TabType = 'diagnostics' | 'openapi-viewer';

export function DevToolsPage() {
  const { sectionColor } = useNavigation();
  const [activeTab, setActiveTab] = useState<TabType>('diagnostics');
  const [apiTestResult, setApiTestResult] = useState<string | null>(null);
  const [isTestingApi, setIsTestingApi] = useState(false);
  const [blobHealthResult, setBlobHealthResult] = useState<string | null>(null);
  const [isTestingBlob, setIsTestingBlob] = useState(false);
  const [debugResult, setDebugResult] = useState<string | null>(null);
  const [isTestingDebug, setIsTestingDebug] = useState(false);
  const [mockDataResult, setMockDataResult] = useState<string | null>(null);
  const [isPopulatingMockData, setIsPopulatingMockData] = useState(false);
  const [clearDataResult, setClearDataResult] = useState<string | null>(null);
  const [isClearingData, setIsClearingData] = useState(false);

  const testApiConnection = async () => {
    setIsTestingApi(true);
    setApiTestResult(null);
    
    // Get API URL from environment
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
    
    // Try multiple API endpoints
    const apiEndpoints = [
      apiUrl, // Production/configured API URL
      '/api', // Relative path (if running on same domain)
      'http://localhost:5001/api', // Local dev server
    ];
    
    try {
      let successfulEndpoint = null;
      
      for (const baseUrl of apiEndpoints) {
        try {
          console.log(`Testing API endpoint: ${baseUrl}`);
          
          const healthResponse = await fetch(`${baseUrl}/health`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
          
          const apiResponse = await fetch(`${baseUrl}`, {
            method: 'GET', 
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (healthResponse.ok && apiResponse.ok) {
            const healthData = await healthResponse.json();
            const apiData = await apiResponse.json();
            
            successfulEndpoint = baseUrl;
            setApiTestResult(`✅ API Working! Endpoint: ${baseUrl}, Health: ${healthData.message}, Version: ${apiData.version}, Database: ${healthData.database || 'not-connected'}`);
            break;
          }
        } catch (endpointError) {
          console.log(`Endpoint ${baseUrl} failed:`, endpointError);
          continue; // Try next endpoint
        }
      }
      
      if (!successfulEndpoint) {
        setApiTestResult(`❌ All API endpoints failed. Tried: ${apiEndpoints.join(', ')}`);
      }
    } catch (error) {
      setApiTestResult(`❌ API Test Failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTestingApi(false);
    }
  };

  const testBlobHealth = async () => {
    setIsTestingBlob(true);
    setBlobHealthResult(null);
    
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
    
    try {
      const response = await fetch(`${apiUrl}/blob-health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        const checks = data.checks;
        setBlobHealthResult(
          `✅ Blob Storage Healthy!\n` +
          `Token Configured: ${checks.tokenConfigured ? '✅' : '❌'}\n` +
          `Can List Blobs: ${checks.canListBlobs ? '✅' : '❌'}\n` +
          `Can Write Blobs: ${checks.canWriteBlobs ? '✅' : '❌'}\n` +
          `Risk Appetite Versions: ${checks.riskAppetiteVersionsCount || 0}`
        );
      } else {
        setBlobHealthResult(
          `❌ Blob Storage Issues:\n` +
          `Token Configured: ${data.checks?.tokenConfigured ? '✅' : '❌'}\n` +
          `Error: ${data.error || 'Unknown error'}`
        );
      }
    } catch (error) {
      setBlobHealthResult(`❌ Failed to connect to blob health endpoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTestingBlob(false);
    }
  };

  const testDebugEndpoint = async () => {
    setIsTestingDebug(true);
    setDebugResult(null);
    
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
    
    try {
      const response = await fetch(`${apiUrl}/debug`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDebugResult(
          `✅ Debug Info Retrieved!\n` +
          `Timestamp: ${data.timestamp}\n` +
          `Environment: ${data.environment}\n` +
          `Node Version: ${data.nodeVersion}\n` +
          `Memory Usage: ${JSON.stringify(data.memoryUsage)}\n` +
          `Uptime: ${data.uptime}s\n` +
          `Environment Variables: ${Object.keys(data.env || {}).length} configured`
        );
      } else {
        setDebugResult(`❌ Debug endpoint returned error: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setDebugResult(`❌ Failed to connect to debug endpoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTestingDebug(false);
    }
  };

  const populateMockData = async () => {
    setIsPopulatingMockData(true);
    setMockDataResult(null);

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

    try {
      const response = await fetch(`${apiUrl}/dev/populate-mock-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        const resultLines = Object.entries(data.results).map(([key, value]) => `${key}: ${value}`);
        setMockDataResult(
          `✅ Mock data populated successfully!\n\n${resultLines.join('\n')}\n\nRefresh your pages to see the new data.`
        );
      } else {
        setMockDataResult(`❌ Failed to populate mock data: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setMockDataResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPopulatingMockData(false);
    }
  };

  const clearAllData = async () => {
    setIsClearingData(true);
    setClearDataResult(null);

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

    try {
      const response = await fetch(`${apiUrl}/dev/clear-all-data`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        const resultLines = Object.entries(data.results).map(([key, value]) => `${key}: ${value}`);
        setClearDataResult(
          `✅ All blob storage cleared successfully!\n\n${resultLines.join('\n')}\n\nAll data has been removed.`
        );
      } else {
        setClearDataResult(`❌ Failed to clear data: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setClearDataResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsClearingData(false);
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className={cn("text-3xl font-bold", sectionColor)}>
          Developer Tools
        </h1>
        <p className="text-neutral-400 mt-2">
          Internal development and debugging utilities
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-800">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('diagnostics')}
            className={cn(
              "pb-3 px-1 border-b-2 transition-colors text-sm font-medium",
              activeTab === 'diagnostics'
                ? "border-primary-500 text-primary-400"
                : "border-transparent text-neutral-400 hover:text-neutral-300"
            )}
          >
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              Diagnostics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('openapi-viewer')}
            className={cn(
              "pb-3 px-1 border-b-2 transition-colors text-sm font-medium",
              activeTab === 'openapi-viewer'
                ? "border-primary-500 text-primary-400"
                : "border-transparent text-neutral-400 hover:text-neutral-300"
            )}
          >
            <div className="flex items-center gap-2">
              <FileCode className="w-4 h-4" />
              OpenAPI Spec
            </div>
          </button>
        </div>
      </div>

      {/* Diagnostics Tab */}
      {activeTab === 'diagnostics' && (
        <div className="space-y-8">
          {/* API Connectivity Test */}
          <div className="card border-l-4 border-primary-500">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-primary-500/20">
            <Database className="w-6 h-6 text-primary-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">API Connectivity Test</h3>
            <p className="text-neutral-400 text-sm">
              Test connection to backend API endpoints. Tries multiple URLs including local dev, production, and relative paths.
            </p>
          </div>
          <button
            onClick={testApiConnection}
            disabled={isTestingApi}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isTestingApi ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Test API
              </>
            )}
          </button>
        </div>

        {apiTestResult && (
          <div className={cn(
            "p-4 rounded-lg border",
            apiTestResult.startsWith('✅') 
              ? "bg-success-500/10 border-success-500/30" 
              : "bg-danger-500/10 border-danger-500/30"
          )}>
            <div className="flex items-start gap-3">
              {apiTestResult.startsWith('✅') ? (
                <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm font-mono text-white leading-relaxed break-all">
                {apiTestResult}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Blob Storage Health Check */}
      <div className="card border-l-4 border-cyan-500">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-cyan-500/20">
            <HardDrive className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">Blob Storage Health</h3>
            <p className="text-neutral-400 text-sm">
              Test Vercel Blob Storage configuration for risk appetite version persistence. Checks token config, read/write permissions, and version count.
            </p>
          </div>
          <button
            onClick={testBlobHealth}
            disabled={isTestingBlob}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isTestingBlob ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <HardDrive className="w-4 h-4" />
                Test Blob
              </>
            )}
          </button>
        </div>

        {blobHealthResult && (
          <div className={cn(
            "p-4 rounded-lg border",
            blobHealthResult.startsWith('✅') 
              ? "bg-success-500/10 border-success-500/30" 
              : "bg-danger-500/10 border-danger-500/30"
          )}>
            <div className="flex items-start gap-3">
              {blobHealthResult.startsWith('✅') ? (
                <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <pre className="text-sm font-mono text-white leading-relaxed whitespace-pre-wrap">
                {blobHealthResult}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Debug Endpoint Test */}
      <div className="card border-l-4 border-orange-500">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-orange-500/20">
            <Bug className="w-6 h-6 text-orange-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">Debug Information</h3>
            <p className="text-neutral-400 text-sm">
              Get server environment details, memory usage, uptime, and configuration status. Useful for troubleshooting deployment issues.
            </p>
          </div>
          <button
            onClick={testDebugEndpoint}
            disabled={isTestingDebug}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isTestingDebug ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Bug className="w-4 h-4" />
                Get Debug Info
              </>
            )}
          </button>
        </div>

        {debugResult && (
          <div className={cn(
            "p-4 rounded-lg border",
            debugResult.startsWith('✅') 
              ? "bg-success-500/10 border-success-500/30" 
              : "bg-danger-500/10 border-danger-500/30"
          )}>
            <div className="flex items-start gap-3">
              {debugResult.startsWith('✅') ? (
                <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <pre className="text-sm font-mono text-white leading-relaxed whitespace-pre-wrap">
                {debugResult}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Populate Mock Data */}
      <div className="card border-l-4 border-purple-500">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-lg bg-purple-500/20">
            <Database className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">Populate Mock Data</h3>
            <p className="text-neutral-400 text-sm">
              Populate all blob storage with comprehensive mock data including staff, skills, customers, suppliers, processes, and risk categories. Useful for testing and demos.
            </p>
            <div className="mt-3 p-3 rounded bg-warning-500/10 border border-warning-500/30">
              <p className="text-warning-400 text-xs font-medium">
                ⚠️ This will overwrite existing data in blob storage. Use with caution!
              </p>
            </div>
          </div>
          <button
            onClick={populateMockData}
            disabled={isPopulatingMockData}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
          >
            {isPopulatingMockData ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Populating...
              </>
            ) : (
              <>
                <Database className="w-4 h-4" />
                Populate Mock Data
              </>
            )}
          </button>
        </div>

        {mockDataResult && (
          <div className={cn(
            "p-4 rounded-lg border",
            mockDataResult.startsWith('✅') 
              ? "bg-success-500/10 border-success-500/30" 
              : "bg-danger-500/10 border-danger-500/30"
          )}>
            <div className="flex items-start gap-3">
              {mockDataResult.startsWith('✅') ? (
                <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <pre className="text-sm font-mono text-white leading-relaxed whitespace-pre-wrap">
                {mockDataResult}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Clear All Data */}
      <div className="card border-l-4 border-red-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-red-500/20">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">Clear All Saved Data</h3>
            <p className="text-neutral-400 text-sm">
              Delete all data from blob storage including staff, skills, customers, suppliers, processes, risk categories, and risk appetite versions. This action cannot be undone.
            </p>
            <div className="mt-3 p-3 rounded bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 text-xs font-medium">
                🔥 DANGER: This will permanently delete ALL data from blob storage!
              </p>
            </div>
          </div>
          <button
            onClick={clearAllData}
            disabled={isClearingData}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-red-600 hover:bg-red-700"
          >
            {isClearingData ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Clearing...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Clear All Data
              </>
            )}
          </button>
        </div>

        {clearDataResult && (
          <div className={cn(
            "p-4 rounded-lg border",
            clearDataResult.startsWith('✅') 
              ? "bg-success-500/10 border-success-500/30" 
              : "bg-danger-500/10 border-danger-500/30"
          )}>
            <div className="flex items-start gap-3">
              {clearDataResult.startsWith('✅') ? (
                <CheckCircle className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <pre className="text-sm font-mono text-white leading-relaxed whitespace-pre-wrap">
                {clearDataResult}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Environment Info */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-cyan-500/20">
            <Terminal className="w-5 h-5 text-cyan-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Environment Information</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">Mode</span>
            <span className="text-sm text-white font-mono">{import.meta.env.MODE}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">API URL</span>
            <span className="text-sm text-white font-mono">{import.meta.env.VITE_API_URL || 'Not configured'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">Base URL</span>
            <span className="text-sm text-white font-mono">{import.meta.env.BASE_URL}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">Dev Mode</span>
            <span className="text-sm text-white font-mono">{import.meta.env.DEV ? 'true' : 'false'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-sm text-neutral-400">SSR Mode</span>
            <span className="text-sm text-white font-mono">{import.meta.env.SSR ? 'true' : 'false'}</span>
          </div>
        </div>
      </div>

      {/* Build Info */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Build Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">Node Version</span>
            <span className="text-sm text-white font-mono">{navigator.userAgent.match(/Node\/([^\s]+)/)?.[1] || 'Browser'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-800">
            <span className="text-sm text-neutral-400">User Agent</span>
            <span className="text-sm text-white font-mono text-right max-w-md truncate">{navigator.userAgent}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-sm text-neutral-400">Screen Resolution</span>
            <span className="text-sm text-white font-mono">{window.screen.width} × {window.screen.height}</span>
          </div>
        </div>
      </div>

      {/* Access Info */}
      <div className="p-4 bg-neutral-900 border border-neutral-800 rounded-lg">
        <div className="flex items-center justify-center gap-2">
          <Lock className="w-4 h-4 text-neutral-500" />
          <p className="text-xs text-neutral-500 text-center">
            DevTools is only accessible via Command/Ctrl + Click on the Line4 logo
          </p>
        </div>
      </div>
        </div>
      )}

      {/* OpenAPI Viewer Tab */}
      {activeTab === 'openapi-viewer' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">OpenAPI Specification</h2>
              <p className="text-sm text-neutral-400 mt-1">
                Live auto-generated API documentation from JSDoc comments - try requests directly from this interface
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                  LIVE: Auto-generated from source
                </span>
              </p>
            </div>
            <a
              href="https://api.line4.ai/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline text-sm"
            >
              Open in New Tab
            </a>
          </div>

          {/* Swagger UI Container */}
          <div className="swagger-container bg-neutral-950 rounded-lg border border-neutral-800 overflow-hidden">
            <SwaggerUI
              url="https://api.line4.ai/api/docs?format=json"
              deepLinking={true}
              displayRequestDuration={true}
              filter={true}
              tryItOutEnabled={true}
              persistAuthorization={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
